<template>
  <v-container fluid class="worker-role-form mt-3 mx-1 centered-tabs" v-show="showComponent">
    <v-form ref="form">
      <v-row>
        <v-col cols="12">
          <h2 class="primary--text font-weight-bold pt-5 pl-2">
            {{ edit ? $t('book/admin:facility-type.edit.titles.edit') : $t('book/admin:facility-type.edit.titles.add') }}
          </h2>

          <v-card
              elevation="0"
              flat
              tile
          >
            <v-card-text>
              <v-row justify="space-around">
                <v-col cols="12" md="5">
                  <h4 class="primary--text font-weight-medium">{{ $t('common:general') }}</h4>
                  <!-- FacilityType name -->
                  <v-text-field
                      outlined
                      v-model="facilityType.name"
                      clearable
                      :label="$t('common:name')"
                      :rules="[ruleRequired($t('utils:rules.required'))]"
                      tabindex="1"
                  />

                  <!-- FacilityType description -->
                  <v-textarea
                      outlined
                      clearable
                      v-model="facilityType.description"
                      :label="$t('common:description')"
                      tabindex="2"
                  />
                  <v-select
                      :items="[{id: 'elevator', name: 'Elevator'}, {id: 'escalator', name: 'Escalator'}, {id: 'walkway', name: 'Walkway'}]"
                      item-text="name"
                      item-value="id"
                      :menu-props="{ bottom: true, offsetY: true }"
                      outlined
                      label="Data Tabs"
                      clearable
                      multiple
                      chips
                      deletable-chips
                      tabindex="3"
                      v-model="facilityType.dataTabs"
                  />
                  <v-select
                      :items="restRoles"
                      item-text="name"
                      item-value="id"
                      :menu-props="{ bottom: true, offsetY: true }"
                      v-model="facilityType.workerRoles"
                      outlined
                      :label="$t('book/admin:facility-type.edit.labels.required-roles')"
                      clearable
                      multiple
                      chips
                      :rules="[ruleRequiredMultiple($t('utils:rules.multiselect', {name : $t('book/admin:facility-type.edit.dialog-part-3')}))]"
                      deletable-chips
                      tabindex="3"
                  />

                  <!-- Multiple select docu role with ability to select all with one click -->
                  <v-select
                      :items="restDocuRules"
                      item-text="translatedName"
                      item-value="id"
                      :menu-props="{ bottom: true, offsetY: true }"
                      v-model="facilityType.documentationRules"
                      outlined
                      :label="$t('book/admin:facility-type.edit.labels.required-docs')"
                      clearable
                      multiple
                      chips
                      :rules="[ruleRequiredMultiple($t('utils:rules.multiselect', {name : $t('book/admin:facility-type.edit.dialog-part-4')}))]"
                      deletable-chips
                      tabindex="4"
                  >
                    <template v-slot:prepend-item>
                      <v-list-item @click="toggle">
                        <v-list-item-action>
                          <v-icon :color="facilityType.documentationRules.length > 0 ? 'primary' : ''">
                            {{ docuRoleIcon }}
                          </v-icon>
                        </v-list-item-action>
                        <v-list-item-content v-if="allDocuRolesSelected">
                          {{ $t('buttons:deselect-all') }}
                        </v-list-item-content>
                        <v-list-item-content v-else>
                          {{ $t('buttons:select-all') }}
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-select>

                  <!-- Multiple select contract type with ability to select all with one click -->
                  <v-select
                      :items="restContractTypes"
                      item-text="translatedName"
                      item-value="name"
                      :menu-props="{ bottom: true, offsetY: true }"
                      v-model="facilityType.contractTypes"
                      outlined
                      :label="$t('book/admin:facility-type.edit.labels.required-contracts')"
                      clearable
                      multiple
                      chips
                      :rules="[ruleRequiredMultiple($t('utils:rules.multiselect', {name : $t('book/admin:facility-type.edit.dialog-part-4')}))]"
                      deletable-chips
                      tabindex="5"
                  >
                    <template v-slot:prepend-item>
                      <v-list-item @click="toggleContracts">
                        <v-list-item-action>
                          <v-icon :color="facilityType.contractTypes.length > 0 ? 'primary' : ''">
                            {{ contractTypeIcon }}
                          </v-icon>
                        </v-list-item-action>
                        <v-list-item-content v-if="allContractsSelected">
                          {{ $t('buttons:deselect-all') }}
                        </v-list-item-content>
                        <v-list-item-content v-else>
                          {{ $t('buttons:select-all') }}
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-select>
                </v-col>

                <v-col cols="12" md="5" v-show="edit" style="height: 300px;">
                  <h4 class="primary--text font-weight-medium">Icon</h4>
                  <DragAndDropFile
                      ref="dragAndDrop"
                      @fileChange="uploadIcon($event)"
                      notRequired
                      custom-height="300px"
                      preview-height="64px"
                      preview-width="64px"
                  />
                  <p class="mt-2">{{ $t('book/admin:facility-type.edit.labels.upload-tip') }}</p>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12">
          <v-card
              elevation="0"
              flat
              tile
          >
            <v-card-text class="pa-5 pb-4">
              <BaseButton
                  color="primary"
                  :btn-text="$t('buttons:save')"
                  @clickedButton="saveFacilityType()"
              />

              <TwoButtonDialog
                  v-if="edit"
                  ref="deleteButton"
                  :btn-text="$t('buttons:delete')"
                  color="error"
                  :dialog-title="$t('dialog:delete-btn-title', {name : $t('book/admin:facility-type.edit.dialog-part-1')})"
                  :dialog-text="$t('dialog:delete-btn-dialog', {name : $t('book/admin:facility-type.edit.dialog-part-2')})"
                  :button1-text="$t('buttons:cancel')"
                  :button2-text="$t('buttons:delete')"
                  @clickedButton1="$refs.deleteButton.showDialog = false"
                  @clickedButton2="deleteFacilityType()"
              />

              <DirtyConfirmButtonDialog
                  ref="dirtyDialog"
                  :btn-text="$t('buttons:back')"
                  color="grey"
                  :dialog-title="$t('dialog:dirty-save-btn-title')"
                  :dialog-text="$t('dialog:dirty-save-btn-dialog')"
                  :button1-text="$t('dialog:dirty-save-btn-1-text')"
                  :button2-text="$t('dialog:dirty-save-btn-2-text')"
                  @clickedButton="$router.go(-1)"
                  :dataToWatch="facilityType"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>

import FacilityTypeService from "@/modules/book/services/facility-type-service";
import WorkerRoleService from "@/modules/core/services/worker-role-service";
import DocumentationRuleService from "@/modules/book/services/documentation-rule-service";

import Utils from "@/modules/core/services/utils";
import DataUtils from "@/modules/core/services/data-utils";
import {ruleRequired, ruleRequiredMultiple} from "@/modules/core/services/rules";

import FacilityType from "@/modules/book/data/facility-type";

import DirtyConfirmButtonDialog from "@/modules/core/components/TwoButtonDialog/DirtyConfirmButtonDialog";
import BaseButton from "@/modules/core/components/Base/BaseButton";
import TwoButtonDialog from "@/modules/core/components/TwoButtonDialog/TwoButtonDialog";
import DragAndDropFile from "@/modules/core/components/DragAndDropFile";
import InfoService from "@/modules/core/services/info-service";

export default {
  name: 'FacilityTypeForm',
  components: {
    DragAndDropFile,
    TwoButtonDialog,
    DirtyConfirmButtonDialog,
    BaseButton
  },
  data() {
    return {
      facilityType: new FacilityType(),
      edit: false,
      isFetchingType: true,
      restRoles: [],
      restDocuRules: [],
      restContractTypes: []
    }
  },
  computed: {
    currentLang() {
      return this.$store.state.translation.language;
    },
    showComponent() {
      if(this.edit) {
        return !this.isFetchingRole;
      } else {
        return true
      }
    },

    // check if user has selected all docu roles
    allDocuRolesSelected() {
      return this.facilityType.documentationRules.length === this.restDocuRules.length;
    },

    // check if user has only selected a few docu roles
    someDocuRolesSelected() {
      return this.facilityType.documentationRules.length > 0 && !this.allDocuRolesSelected;
    },

    allContractsSelected() {
      return this.facilityType.contractTypes.length === this.restContractTypes.length;
    },

    someContractsSelected() {
      return this.facilityType.contractTypes.length > 0 && !this.allContractsSelected;
    },

    // set icon for 'select all'
    docuRoleIcon() {
      if (this.allDocuRolesSelected) return 'disabled_by_default'
      else if (this.someDocuRolesSelected) return 'indeterminate_check_box'
      else return 'check_box_outline_blank'
    },

    // set icon for 'select all' for contract types
    contractTypeIcon() {
      if (this.allContractsSelected) return 'disabled_by_default'
      else if (this.someContractsSelected) return 'indeterminate_check_box'
      else return 'check_box_outline_blank'
    }
  },
  methods: {
    // select/deselect all docu roles with one click
    toggle() {
      this.$nextTick(() => {
        if(this.allDocuRolesSelected) {
          this.facilityType.documentationRules = [];
        } else {
          this.facilityType.documentationRules = [];
          for (let i = 0; i < this.restDocuRules.length; i++) {
            this.facilityType.documentationRules.push(this.restDocuRules[i].id);
          }
        }
      })
    },

    // select/deselect all contract types with one click
    toggleContracts() {
      this.$nextTick(() => {
        if(this.allContractsSelected) {
          this.facilityType.contractTypes = [];
        } else {
          this.facilityType.contractTypes = [];
          for (let i = 0; i < this.restContractTypes.length; i++) {
            this.facilityType.contractTypes.push(this.restContractTypes[i].name);
          }
        }
      })
    },

    saveFacilityType() {
      if (!this.$refs.form.validate()) {
        // TODO scroll to specific form field which doesnt require the rules
        window.scrollTo(0, 0);
        return;
      }

      if(!this.edit) {
        FacilityTypeService.postToAPI(this.facilityType,
          () => {
            Utils.showSuccessSnackbar(this.$t('snackbar:add-success', {name: this.$t('book/admin:facility-type.common.type')}));
            this.$router.go(-1)
          },
          () => {
            Utils.showFailureSnackbar(this.$t('snackbar:add-failure', {name: this.$t('book/admin:facility-type.common.type')}))
          }
        )
      } else {
        FacilityTypeService.putToAPI(this.facilityType,
          () => {
            Utils.showSuccessSnackbar(this.$t('snackbar:edit-success', {name: this.$t('book/admin:facility-type.common.type')}));
            this.$router.go(-1)
          },
          () => {
            Utils.showFailureSnackbar(this.$t('snackbar:edit-failure', {name: this.$t('book/admin:facility-type.common.type')}))
          }
        )
      }
    },

    deleteFacilityType() {
      this.$refs.deleteButton.setButtonSettings(true, true, false, true);

      FacilityTypeService.deleteFromAPI(this.facilityType,
        () => {
          Utils.showSuccessSnackbar(this.$t('snackbar:delete-success', {name: this.$t('book/admin:facility-type.common.type')}));
          this.$router.go(-1)
        },
        () => {
          this.$refs.deleteButton.setButtonSettings(false, false, false, false);
          this.$refs.deleteButton.showDialog = false;
          Utils.showFailureSnackbar(this.$t('snackbar:delete-failure', {name: this.$t('book/admin:facility-type.common.type')}))
        }
      )
    },

    translateAndSortTypes() {
      for(let j = 0; j < this.restDocuRules.length; j++) {
        this.restDocuRules[j].translatedName = this.$t('enums:facility-documentation-types.' + this.restDocuRules[j].name);
      }

      this.restDocuRules.sort((a, b) => {
        if(a.translatedName < b.translatedName) {
          return -1;
        }

        if(a.translatedName > b.translatedName) {
          return 1;
        }

        return 0;
      })
    },

    uploadIcon(file) {
      if (!file) {
        return;
      }

      FacilityTypeService.uploadIcon(this.facilityType.id, file,
          (event) => {
            this.$refs.dragAndDrop.progress = Math.round((100 * event.loaded) / event.total);
          },
          (res) => {
            Utils.showSuccessSnackbar(this.$t('snackbar:upload-success', {name: "Icon"}))
            if (res.data) {
              let docuToParse = {
                fileType: 'image/*',
                src: process.env.VUE_APP_ROOT_HOST + res.data + "?" + Math.random()
              }

              this.$refs.dragAndDrop.setPreviewSrc(docuToParse);
              this.$refs.dragAndDrop.isUploading = false;
            }
          },
          () => {
            Utils.showFailureSnackbar(this.$t('snackbar:upload-failure', {name: "Icon"}))
          }
      )
    },

    ruleRequired,
    ruleRequiredMultiple
  },
  watch: {
    currentLang() {
      this.$refs.form.resetValidation()
      this.translateAndSortTypes();
    }
  },
  mounted() {
    this.$refs.dirtyDialog.startWatch();
  },
  created() {
    if(this.$route.params.id) {
      this.edit = true;

      FacilityTypeService.readFromAPI(this.$route.params.id,
        (res) => {
          this.facilityType = DataUtils.parseDataToSingleObject(res.data, FacilityType);
          if(this.facilityType == null) {
            this.$router.go(-1);
          } else {
            let docuToParse = {
              fileType: 'image/*',
              src: process.env.VUE_APP_ROOT_HOST + this.facilityType.iconUrl + "?" + Math.random()
            }

            this.$refs.dragAndDrop.setPreviewSrc(docuToParse);
            this.isFetchingType = false;
            this.$refs.dirtyDialog.startWatch();
          }
        },
        (error) => {
          this.$router.push('/404')
          this.$logger.error("Error at getting Facility Type: " + error)
        }
      )
    }

    WorkerRoleService.readAllFromAPI(
      (res) => {
        this.restRoles = res.data;
      },
      (error) => {
        this.$router.push('/404')
        this.$logger.error("Error at getting Worker Roles: " + error)
      }
    )

    // get all docu roles
    DocumentationRuleService.readAllFromAPI(
      (res) => {
        this.restDocuRules = res.data;
        this.translateAndSortTypes()
      },
      (error) => {
        this.$router.push('/404')
        this.$logger.error("Error at getting Documentation Rules: " + error)
      }
    )

    InfoService.readAndTranslateEnum(
        'contract/type',
        'enums:contract-types.',
        (res) => {
          this.restContractTypes = Utils.sort(res, true, 'translatedName')
        }
    )
  }
}
</script>